import { Component, OnInit, OnDestroy } from '@angular/core';
import Swal from 'sweetalert2';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { BinanceWithdrawalService } from '../../services/binance-withdrawal.service';
import {
  BinanceWithdrawal,
  WithdrawalQueryParams,
  ConfirmWithdrawalDto,
  RejectWithdrawalDto,
} from '../../interfaces/binance-withdrawal.interface';
import { networkPagaBinance } from 'src/app/shared/utils/red-cripto';

@Component({
  selector: 'app-admin-withdrawals-usdt',
  templateUrl: './admin-withdrawals-usdt.component.html',
  styleUrls: ['./admin-withdrawals-usdt.component.sass'],
})
export class AdminWithdrawalsUsdtComponent implements OnInit, OnDestroy {
  // Math reference for template
  Math = Math;

  // Umbral para aprobación manual (debe coincidir con el backend)
  readonly MANUAL_APPROVAL_THRESHOLD = 5000;
  /** Retiros que se están reenviando a Binance (evita el doble clic). */
  reenviando = new Set<string>();

  /**
   * TxId del pago que anota el operador al aprobar (opcional). Sin él, el
   * backend cierra el retiro con `MANUAL-<id>`, como siempre.
   */
  txIdPago = '';

  // Data
  withdrawals: BinanceWithdrawal[] = [];
  selectedWithdrawal: BinanceWithdrawal | null = null;

  // Pagination
  pagination = {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  };
  pageSizeOptions = [10, 20, 50, 100];

  // Filter Form
  filterForm: FormGroup;

  // UI State
  loading = false;
  errorMessage = '';
  successMessage = '';

  // Mobile accordion
  expandedCardId: number | null = null;

  // Destroy subject for cleanup
  private destroy$ = new Subject<void>();

  // Modal states
  showConfirmModal = false;
  showRejectModal = false;
  showDetailModal = false;

  // Form data for modals
  rejectForm: FormGroup;

  // Table columns
  displayedColumns = [
    'id',
    'userId',
    'walletAddress',
    'network',
    'requestedAmount',
    'fee',
    'netAmount',
    'approvalType',
    'status',
    'requestedAt',
    'actions',
  ];

  // Status options for filter
  statusOptions = [
    { value: '', label: 'Todos' },
    { value: 'pending', label: 'Pendiente' },
    { value: 'processing', label: 'En Proceso' },
    { value: 'completed', label: 'Completado' },
    { value: 'rejected', label: 'Rechazado' },
    { value: 'cancelled', label: 'Cancelado' },
  ];

  constructor(
    private withdrawalService: BinanceWithdrawalService,
    private fb: FormBuilder,
  ) {
    // Initialize filter form
    this.filterForm = this.fb.group({
      userId: [''],
      status: ['pending'], // como la tarjeta del tablero: pendientes de aprobación
      startDate: [''],
      endDate: [''],
    });

    // Initialize reject form
    this.rejectForm = this.fb.group({
      reason: [''],
    });
  }

  ngOnInit(): void {
    // Subscribe to loading state from service
    this.withdrawalService.loading$
      .pipe(takeUntil(this.destroy$))
      .subscribe((loading) => {
        this.loading = loading;
      });

    // Load initial data
    this.loadWithdrawals();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load withdrawals with current filters and pagination
   */
  loadWithdrawals(): void {
    this.errorMessage = '';
    this.successMessage = '';

    const params: WithdrawalQueryParams = {
      page: this.pagination.page,
      limit: this.pagination.limit,
      ...this.getFiltersFromForm(),
    };

    this.withdrawalService
      .getAllWithdrawals(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {

          this.withdrawals = response.items || [];
          this.pagination = {
            page: response.page || 1,
            limit: response.limit || 20,
            total: response.total || 0,
            totalPages: response.totalPages || 0,
          };

          // Log cada retiro para debug
          this.withdrawals.forEach((w, index) => {
          });
        },
        error: (error) => {
          this.errorMessage = error.message || 'Error al cargar los retiros. Por favor intente nuevamente.';
        },
      });
  }

  /**
   * Extract filters from form and format dates
   */
  getFiltersFromForm(): Partial<WithdrawalQueryParams> {
    const formValue = this.filterForm.value;
    const filters: Partial<WithdrawalQueryParams> = {};

    if (formValue.userId?.trim()) {
      filters.userId = formValue.userId.trim();
    }

    if (formValue.status) {
      filters.status = formValue.status;
    }

    if (formValue.startDate) {
      const startDate = new Date(formValue.startDate);
      filters.startDate = startDate.toISOString().split('T')[0];
    }

    if (formValue.endDate) {
      const endDate = new Date(formValue.endDate);
      filters.endDate = endDate.toISOString().split('T')[0];
    }

    return filters;
  }

  /**
   * Apply filters and reload data
   */
  applyFilters(): void {
    this.pagination.page = 1; // Reset to first page
    this.loadWithdrawals();
  }

  /**
   * Clear all filters and reload
   */
  clearFilters(): void {
    this.filterForm.reset({
      userId: '',
      status: 'pending',
      startDate: '',
      endDate: '',
    });
    this.pagination.page = 1;
    this.loadWithdrawals();
  }

  /**
   * Change page
   */
  onPageChange(page: number): void {
    this.pagination.page = page;
    this.loadWithdrawals();
  }

  /**
   * Change page size
   */
  onPageSizeChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.pagination.limit = parseInt(target.value, 10);
    this.pagination.page = 1; // Reset to first page
    this.loadWithdrawals();
  }

  /**
   * Material paginator change event
   */
  onPaginatorChange(event: any): void {
    this.pagination.page = event.pageIndex + 1;
    this.pagination.limit = event.pageSize;
    this.loadWithdrawals();
  }

  /**
   * Generate page numbers for pagination
   */
  getPageNumbers(): number[] {
    const pages: number[] = [];
    const totalPages = this.pagination.totalPages;
    const currentPage = this.pagination.page;

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push(-1); // ellipsis
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push(-1); // ellipsis
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push(-1); // ellipsis
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push(-1); // ellipsis
        pages.push(totalPages);
      }
    }

    return pages;
  }

  /**
   * Toggle mobile card accordion
   */
  toggleCard(id: number): void {
    this.expandedCardId = this.expandedCardId === id ? null : id;
  }

  /**
   * Open confirm modal
   */
  openConfirmModal(withdrawal: BinanceWithdrawal, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.selectedWithdrawal = withdrawal;
    this.errorMessage = ''; // Clear error message when opening
    this.successMessage = '';
    this.txIdPago = '';
    this.showConfirmModal = true;
  }

  /**
   * ¿Lo paga Binance? Solo los de Tron (30/10/2026). Los de Polygon o Solana
   * los paga el operador a mano y se cierran con "Aprobar": no tienen
   * "Reenviar a Binance", que los mandaría como si fueran una wallet Tron.
   */
  pagaBinance(withdrawal: BinanceWithdrawal | null | undefined): boolean {
    return networkPagaBinance(withdrawal?.network);
  }

  /**
   * Close confirm modal
   */
  closeConfirmModal(): void {
    this.showConfirmModal = false;
    this.selectedWithdrawal = null;
    this.errorMessage = ''; // Clear error message when closing
  }

  /**
   * Confirm/approve withdrawal
   */
  confirmWithdrawal(): void {
    if (!this.selectedWithdrawal) {
      return;
    }

    this.errorMessage = '';
    this.successMessage = '';

    // El TxId del pago, si el operador lo anotó. Sin él, el backend cierra el
    // retiro con `MANUAL-<id>`.
    const txId = this.txIdPago.trim();
    const dto: ConfirmWithdrawalDto = txId ? { binanceTxId: txId } : {};

    this.withdrawalService
      .confirmWithdrawal(this.selectedWithdrawal.id, dto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.successMessage = 'Retiro aprobado.';
          this.closeConfirmModal();
          this.loadWithdrawals();

          // Clear success message after 5 seconds
          setTimeout(() => {
            this.successMessage = '';
          }, 5000);
        },
        error: (error) => {
          this.errorMessage = error.message || 'Error al aprobar el retiro';
        },
      });
  }

  /**
   * Reenvía a Binance un retiro en POR_PROCESAR, previa confirmación. Es una
   * acción aparte de aprobar: el flujo de Binance lo completa al responder. El
   * botón se bloquea mientras se envía para que no salga dos veces.
   */
  async reenviarABinance(withdrawal: BinanceWithdrawal, event?: Event): Promise<void> {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    // El botón no se muestra para otras redes; el backend igual lo rechaza.
    if (!this.pagaBinance(withdrawal)) return;
    const { isConfirmed } = await Swal.fire({
      icon: 'warning',
      title: 'Reenviar a Binance',
      html: `El retiro se enviará a Binance y quedará en proceso hasta que Binance responda.<br>No se deshace. No lo envíes si ya lo pagaste a mano.<br><br>` +
        `<small>Monto: <strong>${this.formatUSDT(withdrawal.requestedAmount)} USDT</strong> · Red: <strong>${withdrawal.network}</strong></small>`,
      showCancelButton: true,
      confirmButtonColor: '#1C593D',
      cancelButtonColor: '#dc2626',
      confirmButtonText: 'Sí, reenviar',
      cancelButtonText: 'Cancelar',
    });
    if (!isConfirmed || this.reenviando.has(withdrawal.id)) return;

    this.reenviando.add(withdrawal.id);
    this.errorMessage = '';
    this.successMessage = '';
    this.withdrawalService
      .resendWithdrawal(withdrawal.id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.reenviando.delete(withdrawal.id)),
      )
      .subscribe({
        next: () => {
          this.successMessage = 'Retiro reenviado a Binance. El estado se actualizará automáticamente.';
          this.loadWithdrawals();
          setTimeout(() => {
            this.successMessage = '';
          }, 5000);
        },
        error: (error) => {
          this.errorMessage = error.message || 'Error al reenviar el retiro';
        },
      });
  }

  /**
   * Open reject modal
   */
  openRejectModal(withdrawal: BinanceWithdrawal, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.selectedWithdrawal = withdrawal;
    this.rejectForm.reset();
    this.errorMessage = ''; // Clear error message when opening
    this.successMessage = '';
    this.showRejectModal = true;
  }

  /**
   * Close reject modal
   */
  closeRejectModal(): void {
    this.showRejectModal = false;
    this.selectedWithdrawal = null;
    this.rejectForm.reset();
    this.errorMessage = ''; // Clear error message when closing
  }

  /**
   * Reject withdrawal
   */
  rejectWithdrawal(): void {
    if (!this.selectedWithdrawal) {
      return;
    }

    const reason = this.rejectForm.get('reason')?.value?.trim() || 'Sin motivo especificado';

    this.errorMessage = '';
    this.successMessage = '';

    const dto: RejectWithdrawalDto = { rejectionReason: reason };

    this.withdrawalService
      .rejectWithdrawal(this.selectedWithdrawal.id, dto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.successMessage = 'Retiro rechazado exitosamente';
          this.closeRejectModal();
          this.loadWithdrawals();

          // Clear success message after 5 seconds
          setTimeout(() => {
            this.successMessage = '';
          }, 5000);
        },
        error: (error) => {
          this.errorMessage = error.message || 'Error al rechazar el retiro';
        },
      });
  }

  /**
   * Open detail modal
   */
  openDetailModal(withdrawal: BinanceWithdrawal, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.errorMessage = '';
    this.successMessage = '';

    this.withdrawalService
      .getWithdrawalDetail(withdrawal.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.selectedWithdrawal = response;
          this.showDetailModal = true;
        },
        error: (error) => {
          this.errorMessage = error.message || 'Error al cargar el detalle del retiro';
        },
      });
  }

  /**
   * Close detail modal
   */
  closeDetailModal(): void {
    this.showDetailModal = false;
    this.selectedWithdrawal = null;
    this.errorMessage = ''; // Clear error message when closing
  }

  /**
   * Get status badge class
   */
  getStatusClass(status: string | undefined): string {
    if (!status) return 'bg-gray-100 text-gray-800';

    const statusClasses: { [key: string]: string } = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-orange-100 text-orange-800',
      completed: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800',
    };
    return statusClasses[status] || 'bg-gray-100 text-gray-800';
  }

  /**
   * Get status label
   */
  getStatusLabel(status: string | undefined): string {
    if (!status) return 'Desconocido';

    const statusLabels: { [key: string]: string } = {
      pending: 'Pendiente',
      processing: 'En Proceso',
      completed: 'Completado',
      rejected: 'Rechazado',
      cancelled: 'Cancelado',
    };
    return statusLabels[status] || status;
  }

  /**
   * Format amount as USDT
   */
  formatUSDT(amount: number | string | undefined): string {
    if (amount === undefined || amount === null) return '$0.00';

    // Convert string to number if needed
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

    if (isNaN(numAmount)) return '$0.00';

    return `$${numAmount.toFixed(2)}`;
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string | undefined): string {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Fecha inválida';
      return date.toLocaleString('es-CL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      return 'Error';
    }
  }

  /**
   * Copy text to clipboard
   */
  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(
      () => {
        this.successMessage = 'Copiado al portapapeles';
        setTimeout(() => {
          this.successMessage = '';
        }, 2000);
      },
      (err) => {
      },
    );
  }

  /**
   * Determina si un retiro requiere aprobación manual basándose en el monto
   */
  requiresManualApproval(amount: number | string | undefined): boolean {
    if (amount === undefined || amount === null) return false;
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return numAmount >= this.MANUAL_APPROVAL_THRESHOLD;
  }

  /**
   * Obtiene el label del tipo de aprobación
   */
  getApprovalTypeLabel(amount: number | string | undefined): string {
    return this.requiresManualApproval(amount) ? 'Manual' : 'Automático';
  }

  /**
   * Obtiene la clase CSS del badge de tipo de aprobación
   */
  getApprovalTypeClass(amount: number | string | undefined): string {
    return this.requiresManualApproval(amount)
      ? 'bg-orange-100 text-orange-800 border border-orange-300'
      : 'bg-blue-100 text-blue-800 border border-blue-300';
  }

  /**
   * Obtiene el ícono del tipo de aprobación
   */
  getApprovalTypeIcon(amount: number | string | undefined): string {
    return this.requiresManualApproval(amount) ? 'person' : 'flash_on';
  }
}
